import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import { StandardizedError } from '../utils/consolidatedErrorHandling.js';
import { useToast } from '../contexts/ToastContext';
import { validateFormStep } from '../utils/wizardValidation.js';

/**
 * Custom hook for API operations with loading states and error handling
 */
export const useApi = () => {
  const { getToken } = useAuth();
  const { showError } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (apiCall, options = {}) => {
    const {
      showErrorToast = true,
      loadingState = true,
      onSuccess = null,
      onError = null
    } = options;

    try {
      if (loadingState) setLoading(true);
      setError(null);

      const result = await apiCall(apiClient);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof StandardizedError ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      
      if (showErrorToast) {
        showError(errorMessage);
      }
      
      if (onError) {
        onError(err);
      }
      
      throw err;
    } finally {
      if (loadingState) setLoading(false);
    }
  }, [apiClient, showError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    execute,
    clearError,
    apiClient
  };
};

/**
 * Hook for managing form submission with API calls
 */
export const useFormSubmit = () => {
  const { execute, loading, error, clearError } = useApi();
  const [validationErrors, setValidationErrors] = useState({});

  const submitForm = useCallback(async (apiCall, formData, validationRules = null) => {
    // Clear previous errors
    setValidationErrors({});
    clearError();

    // Client-side validation if rules provided
    if (validationRules) {
      const errors = validateFormStep(formData, validationRules);
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return false;
      }
    }

    try {
      const result = await execute(apiCall);
      return result;
    } catch (err) {
      // Handle validation errors from server
      if (err instanceof StandardizedError && err.status === 422) {
        try {
          const response = await err.response?.json();
          if (response?.errors) {
            setValidationErrors(response.errors);
          }
        } catch (parseError) {
          // Ignore parsing errors, general error will be shown
        }
      }
      return false;
    }
  }, [execute, clearError]);

  const clearValidationErrors = useCallback(() => {
    setValidationErrors({});
  }, []);

  return {
    submitForm,
    loading,
    error,
    validationErrors,
    clearError,
    clearValidationErrors
  };
};

/**
 * Hook for managing data fetching with loading states
 */
export const useDataFetch = () => {
  const { execute, loading, error, clearError } = useApi();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async (apiCall, options = {}) => {
    const result = await execute(apiCall, {
      ...options,
      onSuccess: (data) => {
        setData(data);
        if (options.onSuccess) {
          options.onSuccess(data);
        }
      }
    });
    return result;
  }, [execute]);

  const refetch = useCallback((apiCall, options = {}) => {
    return fetchData(apiCall, options);
  }, [fetchData]);

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return {
    data,
    loading,
    error,
    fetchData,
    refetch,
    clearData,
    clearError
  };
};

/**
 * Hook for file upload operations with progress tracking
 */
export const useFileUpload = () => {
  const { getToken } = useAuth();
  const { showError, showSuccess } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const uploadFile = useCallback(async (endpoint, file, additionalData = {}, options = {}) => {
    const {
      showSuccessToast = true,
      showErrorToast = true,
      onSuccess = null,
      onError = null
    } = options;

    try {
      setUploading(true);
      setProgress(0);
      setError(null);

      const result = await apiClient.uploadFile(
        endpoint,
        file,
        setProgress,
        additionalData
      );

      if (showSuccessToast) {
        const fileName = file ? file.name : (additionalData.original_filename || 'File');
        showSuccess(`File "${fileName}" uploaded successfully`);
      }

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof StandardizedError ? err.message : 'File upload failed';
      setError(errorMessage);

      if (showErrorToast) {
        showError(errorMessage);
      }

      if (onError) {
        onError(err);
      }

      throw err;
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [apiClient, showError, showSuccess]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    uploadFile,
    uploading,
    progress,
    error,
    clearError
  };
};
